import { useState } from "react";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { exportFtaXlsx, listFta, printFtaPdf } from "@/features/transcripts/api";
import { ArchiveFtaDialog } from "@/features/transcripts/components/archive-fta-dialog";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import type { FtaFormGroup, FtaResult } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { ApiError } from "@/lib/api";
import { formatCents } from "@/lib/utils";

function FtaMark({ value }: { value: "" | "Y" | "M" }) {
  if (value === "Y") {
    return (
      <Badge
        variant="outline"
        title="Qualifies — return filed with a clean penalty and abatement history."
        className="border-success/20 bg-success/10 text-success text-[11px] font-bold uppercase"
      >
        Yes
      </Badge>
    );
  }
  if (value === "M") {
    return (
      <Badge
        variant="outline"
        title="Maybe — qualifies on the penalty-free window, but a prior abatement muddies it."
        className="border-warning/30 bg-warning/10 text-warning text-[11px] font-bold uppercase"
      >
        Maybe
      </Badge>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

function FormTable({ group, showAbatementNote }: { group: FtaFormGroup; showAbatementNote: boolean }) {
  const hasMaybe = group.rows.some((r) => r.fta === "M");
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-foreground text-lg font-semibold">Form {group.form}</h2>
      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Tax Year</TableHead>
              <TableHead>Tax Form</TableHead>
              <TableHead>Filed</TableHead>
              <TableHead className="text-right">Failure To File</TableHead>
              <TableHead className="text-right">Failure To Pay</TableHead>
              <TableHead className="text-right">Failure To Deposit</TableHead>
              <TableHead className="text-right">Penalty Reduced</TableHead>
              <TableHead>Possible FTA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.rows.map((row) => (
              <TableRow key={`${row.taxForm}-${row.taxYearText}`}>
                <TableCell className="font-medium">{row.taxYearText}</TableCell>
                <TableCell>{row.taxForm}</TableCell>
                <TableCell>{row.filed}</TableCell>
                <TableCell className="text-right">{formatCents(row.ftfCents)}</TableCell>
                <TableCell className="text-right">{formatCents(row.ftpCents)}</TableCell>
                <TableCell className="text-right">{formatCents(row.ftdCents)}</TableCell>
                <TableCell className="text-right">{formatCents(row.penaltyReducedCents)}</TableCell>
                <TableCell>
                  <FtaMark value={row.fta} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {showAbatementNote && hasMaybe && (
        <p className="text-muted-foreground text-sm">
          A <strong>Maybe</strong> means the taxpayer clears the penalty-free window but a prior
          penalty reduction (e.g. TC 271) may reflect an earlier abatement — confirm before
          requesting.
        </p>
      )}
    </div>
  );
}

function GuidelinesSidebar() {
  return (
    <Card className="h-fit p-5">
      <h2 className="text-foreground mb-2 text-base font-semibold">FTA Guidelines</h2>
      <ul className="text-muted-foreground flex list-disc flex-col gap-2 pl-4 text-sm">
        <li>
          A taxpayer generally qualifies for a First-Time Abatement when the three tax years (or
          twelve quarters, for quarterly forms) immediately before the penalty period are clean —
          no penalties assessed.
        </li>
        <li>
          A prior penalty reduction within that window (e.g. TC 271) downgrades a match to
          &ldquo;Maybe&rdquo; — the taxpayer may have already used an abatement, so reasonable-cause
          may be needed instead.
        </li>
        <li>The return must be filed (or the balance being resolved) to pursue an abatement.</li>
      </ul>
    </Card>
  );
}

export default function ClientFtaPage() {
  const { client, forWho, dataVersion } = useTranscriptsContext();
  const [downloading, setDownloading] = useState(false);
  const state = useResource<FtaResult>(
    () => listFta(client.id, forWho),
    [client.id, forWho, dataVersion],
  );

  async function withBusy(fn: () => Promise<void>) {
    setDownloading(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Download failed.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 text-3xl font-bold">First-Time Abatement Analysis</h1>
        <p className="text-muted-foreground max-w-2xl">
          Penalty abatement eligibility per tax form and year, based on filing history and prior
          abatements.
        </p>
      </div>

      <AsyncContent state={state}>
        {(result) => (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={downloading || result.groups.length === 0}
                onClick={() => withBusy(() => exportFtaXlsx(client.id, forWho))}
              >
                <Download /> Export to Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={downloading || result.groups.length === 0}
                onClick={() => withBusy(() => printFtaPdf(client.id, forWho))}
              >
                <Printer /> Print PDF
              </Button>
              <ArchiveFtaDialog
                clientId={client.id}
                forWho={forWho}
                disabled={result.groups.length === 0}
              />
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
              <div className="flex flex-col gap-6">
                {result.groups.length === 0 ? (
                  <Card className="text-muted-foreground p-8 text-center">
                    No tax records for this selection.
                  </Card>
                ) : (
                  result.groups.map((group) => (
                    <FormTable key={group.form} group={group} showAbatementNote={result.hasMaybe} />
                  ))
                )}

                {result.possibleFta && (
                  <p className="text-muted-foreground text-sm">
                    If the taxpayer is current with filing and owes tax that is being resolved (e.g.
                    an installment agreement), the flagged period(s) may qualify for a First-Time
                    Abatement.
                  </p>
                )}
              </div>
              <GuidelinesSidebar />
            </div>
          </div>
        )}
      </AsyncContent>
    </div>
  );
}
